import React, { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  RefreshCcw,
  Download,
  Plus,
  Search,
  Calendar,
  ChevronDown,
  Eye,
} from "lucide-react";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import PageWrapper from "components/PageWrapper";
import { Button } from "components/ui/button";
import Spinner from "components/Spinner";
import { All_DISTRIBUTOR } from "constants/appConstant";
import iziToast from "izitoast";
import { formatDate } from "utils/helper";
import SamiSpin from "components/SamiSpin";
import {
  getMyOrdersAPI,
  getDistributorSalesOrderIds,
  getSalesOrderHistory,
  deleteOrdersAPI,
  refreshOrdersAPI,
  downloadRawOrderAPI,
} from "actions/ordersAction";

const autoSizeColumns = (ws) => {
  ws.columns.forEach((column) => {
    let maxLength = 10;
    column.eachCell({ includeEmpty: true }, (cell) => {
      const len = cell.value != null ? String(cell.value).length : 0;
      if (len > maxLength) maxLength = len;
    });
    column.width = Math.min(maxLength + 2, 60);
  });
};

const addSummarySheet = (wb, source) => {
  const ws = wb.addWorksheet("Summary");
  const header = ws.addRow(["Field", "Value"]);
  header.font = { bold: true };

  const walk = (obj, prefix = "") => {
    Object.entries(obj || {}).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value == null || typeof value !== "object") {
        ws.addRow([path, value == null ? "" : value]);
      } else if (Array.isArray(value)) {
        ws.addRow([path, `[${value.length} item${value.length === 1 ? "" : "s"}]`]);
      } else {
        walk(value, path);
      }
    });
  };
  walk(source);
  autoSizeColumns(ws);
};

const addLineItemsSheet = (wb, items, columns, sheetName = "Line Items", totalConfig = null) => {
  if (!items || items.length === 0) return;
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  const header = ws.addRow(columns.map((c) => c.label));
  header.font = { bold: true };
  items.forEach((item) => {
    ws.addRow(columns.map((c) => c.value(item) ?? ""));
  });

  if (totalConfig) {
    const totalColIdx = columns.findIndex((c) => c.label === totalConfig.column);
    if (totalColIdx >= 0) {
      const sum = items.reduce((acc, item) => acc + Number(columns[totalColIdx].value(item) || 0), 0);
      const row = Array(columns.length).fill("");
      if (totalColIdx > 0) row[totalColIdx - 1] = "Total";
      row[totalColIdx] = totalConfig.currency
        ? `${totalConfig.currency} ${sum.toFixed(2)}`
        : Number(sum.toFixed(2));
      const totalRow = ws.addRow(row);
      totalRow.font = { bold: true };
    }
  }

  autoSizeColumns(ws);
};

const exportRawOrderToExcel = async (data, type, order) => {
  
  const wb = new ExcelJS.Workbook();
  const distributor = type === "Mouser" ? "Mouser" : "Digikey";
  const orderId = order?.web_order_id || "";
  const salesOrderId =
    order?.mouser_data?.sales_order_id ||
    order?.digikey_data?.sales_order_id ||
    "";
  const poNumber =
    order?.mouser_data?.po_number || order?.digikey_data?.order_id || "";
  const invoiceNumber =
    order?.mouser_data?.invoice_number ||
    order?.digikey_data?.invoice_id ||
    "";
  const trackingNumberRaw =
    order?.mouser_data?.tracking_number ||
    order?.digikey_data?.tracking_number ||
    "";
  const shipMethod =
    order?.mouser_data?.shipping_method_name ||
    order?.digikey_data?.delivery_detail?.ShippingMethodName ||
    "";
  const trackingNumber = trackingNumberRaw && shipMethod
    ? `${trackingNumberRaw} (${shipMethod})`
    : trackingNumberRaw || shipMethod || "";
  const orderStatus =
    order?.mouser_data?.order_status ||
    order?.digikey_data?.order_status ||
    "";
  const orderedDateRaw =
    order?.mouser_data?.order_date ||
    order?.digikey_data?.order_date ||
    order?.created_at ||
    "";
  const orderedDate = orderedDateRaw ? formatDate(orderedDateRaw) : "";

  const headerColumns = [
    { label: "Distributor", value: () => distributor },
    { label: "Order ID", value: () => orderId },
    { label: "Ordered Date", value: () => orderedDate },
    { label: "Sales Order ID", value: () => salesOrderId },
    { label: "PO Number", value: () => poNumber },
    { label: "Invoice Number", value: () => invoiceNumber },
    { label: "Tracking Number", value: () => trackingNumber },
    { label: "Order Status", value: () => orderStatus },
  ];

  const currency =
    order?.mouser_data?.currency_code ||
    order?.digikey_data?.currency_code ||
    "";

  if (type === "Mouser") {
    const lines = data?.OrderLines || [];

    addLineItemsSheet(
      wb,
      lines,
      [
        ...headerColumns,
        { label: "Mouser Part Number", value: (l) => l.ProductInfo?.MouserPartNumber || l.MouserPartNumber || "" },
        { label: "MFR Part Number", value: (l) => l.ProductInfo?.ManufacturerPartNumber || l.MfrPartNumber || "" },
        { label: "Manufacturer", value: (l) => l.ProductInfo?.Manufacturer || "" },
        { label: "Description", value: (l) => l.ProductInfo?.Description || l.Description || "" },
        { label: "Quantity", value: (l) => l.Quantity ?? 0 },
        { label: "Unit Price", value: (l) => l.UnitPrice ?? 0 },
        { label: "Extended Price", value: (l) => l.ExtendedPrice ?? (Number(l.UnitPrice || 0) * Number(l.Quantity || 0)) },
      ],
      "Line Items",
      { column: "Extended Price", currency },
    );
  } else if (type === "Digikey") {
    const lines = data?.LineItems || [];

    addLineItemsSheet(
      wb,
      lines,
      [
        ...headerColumns,
        { label: "DigiKey Part Number", value: (l) => l.DigiKeyProductNumber || "" },
        { label: "MFR Part Number", value: (l) => l.ManufacturerProductNumber || "" },
        { label: "Description", value: (l) => l.Description || "" },
        { label: "Qty Ordered", value: (l) => l.QuantityOrdered ?? 0 },
        { label: "Qty Shipped", value: (l) => l.QuantityShipped ?? 0 },
        { label: "Qty Backorder", value: (l) => l.QuantityBackOrder ?? 0 },
        { label: "Unit Price", value: (l) => l.UnitPrice ?? 0 },
        { label: "Total Price", value: (l) => l.TotalPrice ?? 0 },
      ],
      "Line Items",
      { column: "Total Price", currency },
    );
  }

  addSummarySheet(wb, data);

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `order-${order?.web_order_id || order?.id}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Element14 has no raw-order API, so the export is built from the element14_data
// already saved at checkout and returned in the orders list.
const exportElement14OrderToExcel = async (order) => {
  const data = order?.element14_data || {};
  const lines = data?.order_details || [];
  const currency = data?.currency_code || "";

  const wb = new ExcelJS.Workbook();

  const headerColumns = [
    { label: "Distributor", value: () => "Element14" },
    { label: "Order ID", value: () => order?.web_order_id || data?.order_id || "" },
    { label: "Ordered Date", value: () => (order?.created_at ? formatDate(order.created_at) : "") },
    { label: "Sales Order ID", value: () => data?.sales_order_id || "" },
    { label: "PO Number", value: () => data?.po_number || "" },
    { label: "Order Status", value: () => data?.order_status || "" },
  ];

  addLineItemsSheet(
    wb,
    lines,
    [
      ...headerColumns,
      { label: "Part Number", value: (l) => l.part_number || "" },
      { label: "MFR Part Number", value: (l) => l.mfr_part_number || "" },
      { label: "Manufacturer", value: (l) => l.manufacturer || "" },
      { label: "Description", value: (l) => l.description || "" },
      { label: "Quantity", value: (l) => l.qty ?? 0 },
      { label: "Unit Price", value: (l) => l.unit_price ?? 0 },
      { label: "Extended Price", value: (l) => l.extended_price ?? (Number(l.unit_price || 0) * Number(l.qty || 0)) },
    ],
    "Line Items",
    { column: "Extended Price", currency },
  );

  addSummarySheet(wb, data);

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `order-${order?.web_order_id || order?.id}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// TME and Rutronik have no raw-order API, so the export is built from the
// *_data already saved at checkout and returned in the orders list (same shape
// as Element14). Both distributors share this builder - only the label and the
// source key differ.
const exportEmailOrderToExcel = async (order, distributorName, orderData) => {
  const data = orderData || {};
  const lines = data?.order_details || [];
  const currency = data?.currency_code || "";

  const wb = new ExcelJS.Workbook();

  const headerColumns = [
    { label: "Distributor", value: () => distributorName },
    { label: "Order ID", value: () => order?.web_order_id || data?.order_id || "" },
    { label: "Ordered Date", value: () => (order?.created_at ? formatDate(order.created_at) : "") },
    { label: "PO Number", value: () => data?.po_number || "" },
    { label: "Order Email", value: () => data?.order_email || "" },
    { label: "Order Status", value: () => data?.order_status || "" },
  ];

  addLineItemsSheet(
    wb,
    lines,
    [
      ...headerColumns,
      { label: "Part Number", value: (l) => l.part_number || "" },
      { label: "MFR Part Number", value: (l) => l.mfr_part_number || "" },
      { label: "Manufacturer", value: (l) => l.manufacturer || "" },
      { label: "Description", value: (l) => l.description || "" },
      { label: "Quantity", value: (l) => l.qty ?? 0 },
      { label: "Unit Price", value: (l) => l.unit_price ?? 0 },
      { label: "Extended Price", value: (l) => l.extended_price ?? (Number(l.unit_price || 0) * Number(l.qty || 0)) },
    ],
    "Line Items",
    { column: "Extended Price", currency },
  );

  addSummarySheet(wb, data);

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `order-${order?.web_order_id || order?.id}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// SmartBuy design-system status pills (orders-styles.css).
const STATUS_PILLS = {
  complete: { label: "Complete", pill: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success" },
  shipped: { label: "Shipped", pill: "bg-s360-info-bg text-[#002E4D]", dot: "bg-s360-info" },
  invoiced: { label: "Invoiced", pill: "bg-s360-violet-50 text-s360-violet-900", dot: "bg-s360-violet" },
  pending: { label: "Pending", pill: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning" },
  backordered: { label: "Backordered", pill: "bg-s360-bg3 text-s360-fg2", dot: "bg-s360-fg3" },
  cancelled: { label: "Cancelled", pill: "bg-s360-danger-bg text-[#700A14]", dot: "bg-s360-danger" },
  other: { label: "—", pill: "bg-s360-bg3 text-s360-fg2", dot: "bg-s360-fg3" },
};

// Map a raw status string to a design-system category key.
const categorizeStatus = (status) => {
  const v = (status || "").toLowerCase();
  if (v.includes("complete") || v.includes("delivered")) return "complete";
  if (v.includes("cancel")) return "cancelled";
  if (v.includes("shipped")) return "shipped";
  if (v.includes("invoiced")) return "invoiced";
  if (v.includes("backorder")) return "backordered";
  if (
    v.includes("pending") ||
    v.includes("processing") ||
    v.includes("received") ||
    v.includes("submitted")
  )
    return "pending";
  return "other";
};

const DIST_WATERMARK = {
  Mouser: { short: "Mou", bg: "bg-[#003478]" },
  Digikey: { short: "DK", bg: "bg-[#CC0000]" },
  Element14: { short: "E14", bg: "bg-[#FF6C0C]" },
  TME: { short: "TME", bg: "bg-[#1F8A5B]" },
  Rutronik: { short: "Rut", bg: "bg-[#00509E]" },
  TI: { short: "TI", bg: "bg-[#C8102E]" },
  Arrow: { short: "Arr", bg: "bg-[#1B1B1B]" },
  TTI: { short: "TTI", bg: "bg-[#0F6CBD]" },
  Avnet: { short: "Avn", bg: "bg-[#5F259F]" },
  Verical: { short: "Ver", bg: "bg-[#2E7D32]" },
};

const StatTile = ({ tone, label, value, foot }) => {
  const accent = {
    violet: "border-t-s360-violet",
    green: "border-t-s360-success",
    amber: "border-t-s360-warning",
    info: "border-t-s360-info",
  }[tone];
  return (
    <div className={`bg-white rounded-md shadow-sm px-4 py-3.5 flex flex-col gap-1 border-t-[3px] ${accent}`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
        {label}
      </span>
      <span className="text-[22px] leading-7 font-bold tracking-[-0.015em] text-s360-ink tabular-nums">
        {value}
      </span>
      <span className="text-[11px] text-s360-fg3">{foot}</span>
    </div>
  );
};

const MyOrdersPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loader, setLoader] = useState(false);
  const [salesHistory, setSalesHistory] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [salesOrderIds, setSalesOrderIds] = useState([]);
  const [error, setError] = useState("");
  const [distributor, setDistributor] = useState(null);
  const [saleId, setSaleId] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [refreshingId, setRefreshingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { state } = useLocation();
  const saleIdFromState = state?.saleId;
  const distributorFromState = state?.distributor;

  const handleViewDetails = (order) => {
    const distributor = order?.mouser_data
      ? "Mouser"
      : order?.digikey_data
      ? "Digikey"
      : order?.element14_data
      ? "Element14"
      : order?.tme_data
      ? "TME"
      : order?.rutronik_data
      ? "Rutronik"
      : order?.ti_data
      ? "TI"
      : order?.arrow_data
      ? "Arrow"
      : order?.tti_data
      ? "TTI"
      : order?.avnet_data
      ? "Avnet"
      : order?.verical_data
      ? "Verical"
      : "";

    if (!distributor) {
      iziToast.error({
        title: "Error",
        message: "Unable to determine the distributor for this order.",
        position: "topCenter",
      });
      return;
    }

    navigate(`/my-orders/detail/${order.id}`, { state: { distributor } });
  };

  const handleRefresh = async (order) => {
    const type = order?.mouser_data
      ? "Mouser"
      : order?.digikey_data
      ? "Digikey"
      : null;

    if (!type) {
      iziToast.error({
        title: "Error",
        message: "Unable to determine the distributor for this order.",
        position: "topCenter",
      });
      return;
    }

    try {
      setRefreshingId(order.id);
      const response = await refreshOrdersAPI(order.id, type);

      if (response.status) {
        getMyOrders();
        iziToast.success({
          title: "Success",
          message: "Order refreshed successfully",
          position: "topCenter",
        });
      } else {
        iziToast.error({
          title: "Error",
          message: response.message || "Failed to refresh order",
          position: "topCenter",
        });
      }
    } catch (error) {
      console.error("Error refreshing order:", error);
      iziToast.error({
        title: "Error",
        message: "Something went wrong while refreshing order",
        position: "topCenter",
      });
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDownload = async (order) => {
    // Element14 has no raw-order API; export from the data already saved at checkout.
    if (order?.element14_data) {
      try {
        setDownloadingId(order.id);
        await exportElement14OrderToExcel(order);
      } catch (error) {
        console.error("Error downloading Element14 order:", error);
        iziToast.error({
          title: "Error",
          message: "Something went wrong while downloading order",
          position: "topCenter",
        });
      } finally {
        setDownloadingId(null);
      }
      return;
    }

    // TME has no raw-order API either; export from the saved checkout data.
    if (order?.tme_data) {
      try {
        setDownloadingId(order.id);
        await exportEmailOrderToExcel(order, "TME", order.tme_data);
      } catch (error) {
        console.error("Error downloading TME order:", error);
        iziToast.error({
          title: "Error",
          message: "Something went wrong while downloading order",
          position: "topCenter",
        });
      } finally {
        setDownloadingId(null);
      }
      return;
    }

    // Rutronik is email-fulfilled like TME; export from the saved checkout data.
    if (order?.rutronik_data) {
      try {
        setDownloadingId(order.id);
        await exportEmailOrderToExcel(order, "Rutronik", order.rutronik_data);
      } catch (error) {
        console.error("Error downloading Rutronik order:", error);
        iziToast.error({
          title: "Error",
          message: "Something went wrong while downloading order",
          position: "topCenter",
        });
      } finally {
        setDownloadingId(null);
      }
      return;
    }

    // Arrow/TTI/Avnet/Verical are email-fulfilled like TME/Rutronik; export from
    // the saved checkout data.
    const mailOrderEntry = [
      ["Arrow", order?.arrow_data],
      ["TTI", order?.tti_data],
      ["Avnet", order?.avnet_data],
      ["Verical", order?.verical_data],
    ].find(([, data]) => data);
    if (mailOrderEntry) {
      const [mailOrderName, mailOrderData] = mailOrderEntry;
      try {
        setDownloadingId(order.id);
        await exportEmailOrderToExcel(order, mailOrderName, mailOrderData);
      } catch (error) {
        console.error(`Error downloading ${mailOrderName} order:`, error);
        iziToast.error({
          title: "Error",
          message: "Something went wrong while downloading order",
          position: "topCenter",
        });
      } finally {
        setDownloadingId(null);
      }
      return;
    }

    const type = order?.mouser_data
      ? "Mouser"
      : order?.digikey_data
      ? "Digikey"
      : null;

    if (!type) {
      iziToast.error({
        title: "Error",
        message: "Unable to determine the distributor for this order.",
        position: "topCenter",
      });
      return;
    }

    try {
      setDownloadingId(order.id);
      const response = await downloadRawOrderAPI(order.id, type);

      if (response.status && response.data) {
        await exportRawOrderToExcel(response.data, type, order);
      } else {
        iziToast.error({
          title: "Error",
          message: response.message || "Failed to download order response",
          position: "topCenter",
        });
      }
    } catch (error) {
      console.error("Error downloading order:", error);
      iziToast.error({
        title: "Error",
        message: "Something went wrong while downloading order",
        position: "topCenter",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleBackToList = () => {
    setSelectedOrder(null);
    setShowDetailView(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSalesOrderIds([]);
    setDistributor(null);
    setSaleId(null);
  };

  const handleSelectDistributor = async (value) => {
    setError("");
    setSaleId(null); // Reset saleId when distributor changes
    setDistributor(value);
    const response = await getDistributorSalesOrderIds(value);

    if (response.status) {
      const formatIds = response.data
        .map((item) => ({
          name: item.sales_order_id,
          value: item.sales_order_id,
        }))
        .filter((item) => item?.value !== null && item?.value !== undefined);
      setSalesOrderIds(formatIds);
    }
  };

  const handleChangeSaleId = (value) => {
    setError("");
    setSaleId(value);
  };

  const handleModal = () => setShowModal(!showModal);

  const getTotalAmount = (order) => {
    console.log("Getting total amount", order?.mouser_data?.total);
    if (order?.mouser_data) {
      const symbol = order?.mouser_data?.currency_code || "";
      return `${symbol} ${Number(order?.mouser_data?.total || 0).toFixed(2)}`;
    } else if (order?.digikey_data) {
      const symbol = order?.digikey_data?.currency_code || "";
      return `${symbol} ${Number(order?.digikey_data?.total || 0).toFixed(2)}`;
    } else if (order?.element14_data) {
      const symbol = order?.element14_data?.currency_code || "";
      return `${symbol} ${Number(order?.element14_data?.total || 0).toFixed(2)}`;
    } else if (order?.tme_data) {
      const symbol = order?.tme_data?.currency_code || "";
      return `${symbol} ${Number(order?.tme_data?.total || 0).toFixed(2)}`;
    } else if (order?.rutronik_data) {
      const symbol = order?.rutronik_data?.currency_code || "";
      return `${symbol} ${Number(order?.rutronik_data?.total || 0).toFixed(2)}`;
    } else if (order?.ti_data) {
      const symbol = order?.ti_data?.currency_code || "";
      return `${symbol} ${Number(order?.ti_data?.total || 0).toFixed(2)}`;
    } else if (order?.arrow_data) {
      const symbol = order?.arrow_data?.currency_code || "";
      return `${symbol} ${Number(order?.arrow_data?.total || 0).toFixed(2)}`;
    } else if (order?.tti_data) {
      const symbol = order?.tti_data?.currency_code || "";
      return `${symbol} ${Number(order?.tti_data?.total || 0).toFixed(2)}`;
    } else if (order?.avnet_data) {
      const symbol = order?.avnet_data?.currency_code || "";
      return `${symbol} ${Number(order?.avnet_data?.total || 0).toFixed(2)}`;
    } else if (order?.verical_data) {
      const symbol = order?.verical_data?.currency_code || "";
      return `${symbol} ${Number(order?.verical_data?.total || 0).toFixed(2)}`;
    }
    return 0;
  };

  const getDistributorName = (order) => {
    if (order?.mouser_data) {
      return "Mouser";
    } else if (order?.digikey_data) {
      return "Digikey";
    } else if (order?.element14_data) {
      return "Element14";
    } else if (order?.tme_data) {
      return "TME";
    } else if (order?.rutronik_data) {
      return "Rutronik";
    } else if (order?.ti_data) {
      return "TI";
    } else if (order?.arrow_data) {
      return "Arrow";
    } else if (order?.tti_data) {
      return "TTI";
    } else if (order?.avnet_data) {
      return "Avnet";
    } else if (order?.verical_data) {
      return "Verical";
    }
    return "";
  };

  const getOrderStatus = (order) =>
    order?.mouser_data?.order_status ||
    order?.digikey_data?.order_status ||
    order?.element14_data?.order_status ||
    order?.tme_data?.order_status ||
    order?.rutronik_data?.order_status ||
    order?.ti_data?.order_status ||
    order?.arrow_data?.order_status ||
    order?.tti_data?.order_status ||
    order?.avnet_data?.order_status ||
    order?.verical_data?.order_status ||
    "N/A";

  const getNumericTotal = (order) => {
    if (order?.mouser_data) return Number(order?.mouser_data?.total || 0);
    if (order?.digikey_data) return Number(order?.digikey_data?.total || 0);
    if (order?.element14_data) return Number(order?.element14_data?.total || 0);
    if (order?.tme_data) return Number(order?.tme_data?.total || 0);
    if (order?.rutronik_data) return Number(order?.rutronik_data?.total || 0);
    if (order?.ti_data) return Number(order?.ti_data?.total || 0);
    if (order?.arrow_data) return Number(order?.arrow_data?.total || 0);
    if (order?.tti_data) return Number(order?.tti_data?.total || 0);
    if (order?.avnet_data) return Number(order?.avnet_data?.total || 0);
    if (order?.verical_data) return Number(order?.verical_data?.total || 0);
    return 0;
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prev) => {
      if (prev.includes(orderId)) {
        return prev.filter((id) => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(myOrders?.order_data?.map((order) => order.id) || []);
    } else {
      setSelectedOrders([]);
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteSelected = async () => {
    if (selectedOrders.length === 0) {
      setError("Please select at least one order to delete");
      return;
    }
    setShowDeleteModal(true); // Show confirmation modal
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    try {
      setLoader(true);
      const response = await deleteOrdersAPI({ ids: selectedOrders });
      if (response.status) {
        setSelectedOrders([]);
        setShowDeleteModal(false);
        getMyOrders(); // Refresh the orders list
        iziToast.success({
          title: "Success",
          message: response.message,
          position: "topCenter",
        });
      } else {
        setError(response.message || "Failed to delete orders");
      }
    } catch (error) {
      setError("Something went wrong while deleting orders");
    } finally {
      setLoader(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedOrders([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!saleId) {
      setError("Sale number is required");
      return;
    }
    const response = await getSalesOrderHistory({
      saleId,
      distributor,
    });
    console.log({ responseData: response.data });

    // Some failures come back as outer success wrapping a Response object
    // with original.status === false — treat those as errors too.
    const innerError =
      response?.data?.original?.status === false
        ? response?.data?.original?.message
        : null;

    if (!response.status || innerError) {
      setSalesHistory(null);
      setError(
        innerError || response?.message || "Failed to fetch order details",
      );
      return;
    }

    const orderId = response?.data?.id;
    if (!orderId) {
      setSalesHistory(null);
      setError("Order details not found for this sales ID");
      return;
    }

    setSalesHistory(response.data);
    navigate(`/my-orders/detail/${orderId}`, {
      state: { distributor },
    });
    handleCloseModal();
  };

  const getMyOrders = async () => {
    try {
      setIsLoading(true);
      const response = await getMyOrdersAPI();
      console.log({ response });
      if (response.status) {
        setMyOrders(response.data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getMyOrders();
  }, []);

  const orderData = myOrders?.order_data || [];

  const counts = {
    all: orderData.length,
    complete: orderData.filter((o) => categorizeStatus(getOrderStatus(o)) === "complete").length,
    shipped: orderData.filter((o) => categorizeStatus(getOrderStatus(o)) === "shipped").length,
    pending: orderData.filter((o) => categorizeStatus(getOrderStatus(o)) === "pending").length,
    cancelled: orderData.filter((o) => categorizeStatus(getOrderStatus(o)) === "cancelled").length,
  };

  const openCount = counts.pending;
  const totalSpend = orderData
    .filter((o) => categorizeStatus(getOrderStatus(o)) !== "cancelled")
    .reduce((sum, o) => sum + getNumericTotal(o), 0);
  const spendCurrency =
    orderData
      .map(
        (o) =>
          o?.mouser_data?.currency_code ||
          o?.digikey_data?.currency_code ||
          o?.element14_data?.currency_code ||
          o?.tme_data?.currency_code ||
          o?.rutronik_data?.currency_code ||
          o?.ti_data?.currency_code ||
          o?.arrow_data?.currency_code ||
          o?.tti_data?.currency_code ||
          o?.avnet_data?.currency_code ||
          o?.verical_data?.currency_code,
      )
      .find(Boolean) || "";

  const filteredOrders = orderData.filter((order) => {
    if (statusFilter !== "all" && categorizeStatus(getOrderStatus(order)) !== statusFilter) {
      return false;
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      order?.id,
      order?.web_order_id,
      order?.mouser_data?.sales_order_id ||
        order?.digikey_data?.sales_order_id ||
        order?.element14_data?.sales_order_id ||
        order?.tme_data?.sales_order_id ||
        order?.rutronik_data?.sales_order_id ||
        order?.ti_data?.sales_order_id ||
        order?.arrow_data?.sales_order_id ||
        order?.tti_data?.sales_order_id ||
        order?.avnet_data?.sales_order_id ||
        order?.verical_data?.sales_order_id,
      order?.mouser_data?.po_number ||
        order?.digikey_data?.order_id ||
        order?.element14_data?.po_number ||
        order?.tme_data?.po_number ||
        order?.rutronik_data?.po_number ||
        order?.ti_data?.po_number ||
        order?.arrow_data?.po_number ||
        order?.tti_data?.po_number ||
        order?.avnet_data?.po_number ||
        order?.verical_data?.po_number,
      order?.mouser_data?.invoice_number || order?.digikey_data?.invoice_id,
      getDistributorName(order),
      order?.mouser_data?.tracking_number || order?.digikey_data?.tracking_number,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });

  const FILTER_TABS = [
    { key: "all", label: "All" },
    { key: "complete", label: "Complete" },
    { key: "shipped", label: "Shipped" },
    { key: "pending", label: "Pending" },
    { key: "cancelled", label: "Cancelled" },
  ];

  return (
    <PageWrapper>
      {/* <MouserOrderTable myOrders={myOrders} isLoading={isLoading} /> */}

      <div className="flex flex-col gap-4">
        {/* Page head */}
        <div className="flex items-end justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
              Orders
            </h1>
            <span className="text-sm text-s360-fg3">
              Manage and track procurement orders across all connected distributors.
            </span>
          </div>
          <button
            onClick={handleModal}
            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-s360-violet text-white text-sm font-semibold hover:bg-s360-violet-600 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Import order
          </button>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile tone="violet" label="Total orders" value={counts.all} foot="all time" />
          <StatTile tone="green" label="Complete" value={counts.complete} foot="delivered & invoiced" />
          <StatTile tone="amber" label="Open" value={openCount} foot="pending · processing" />
          <StatTile
            tone="info"
            label="Spend"
            value={`${spendCurrency} ${totalSpend.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })}`}
            foot="excludes cancelled"
          />
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="inline-flex bg-white border border-s360-stroke1 rounded-md overflow-hidden">
            {FILTER_TABS.map((tab, i) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`h-[34px] px-3 inline-flex items-center gap-1.5 text-xs font-semibold ${
                  i > 0 ? "border-l border-s360-stroke1" : ""
                } ${
                  statusFilter === tab.key
                    ? "bg-s360-violet-50 text-s360-violet-900"
                    : "text-s360-fg2 hover:bg-s360-bg2"
                }`}
              >
                {tab.label}
                <span
                  className={`font-mono text-[10px] px-1.5 py-px rounded-sm ${
                    statusFilter === tab.key ? "bg-s360-violet-100" : "bg-black/[0.06]"
                  }`}
                >
                  {counts[tab.key]}
                </span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 h-9 px-3 bg-white border border-s360-stroke1 border-b-2 rounded-md min-w-[280px] flex-1 focus-within:border-s360-violet text-s360-fg3 focus-within:text-s360-fg1">
            <Search className="w-3.5 h-3.5" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by order ID, PO, sales ID, invoice…"
              className="flex-1 bg-transparent outline-none text-sm text-s360-fg1 placeholder:text-s360-fg-disabled"
            />
          </div>
          <button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white border border-s360-stroke1 text-xs font-semibold text-s360-fg2 hover:bg-s360-bg2">
            <Calendar className="w-3.5 h-3.5" /> All time <ChevronDown className="w-3 h-3" />
          </button>
          <span className="ml-auto text-xs text-s360-fg3">
            Showing <strong className="text-s360-fg1">{filteredOrders.length}</strong> of{" "}
            {orderData.length} orders
          </span>
        </div>

        {/* Table card */}
        <div className="bg-white rounded-md shadow-sm overflow-hidden">
          {selectedOrders.length > 0 && (
            <div className="flex items-center gap-3.5 px-4 py-2.5 bg-s360-violet-50 border-b border-s360-violet-100">
              <span className="text-[13px] text-s360-violet-900">
                <strong className="font-bold">{selectedOrders.length}</strong> order
                {selectedOrders.length !== 1 ? "s" : ""} selected
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteSelected}
                className="h-[30px] flex items-center gap-1"
              >
                Delete ({selectedOrders.length})
              </Button>
              <button
                onClick={() => setSelectedOrders([])}
                className="ml-auto text-xs font-semibold text-s360-fg3 underline underline-offset-2 hover:text-s360-fg1"
              >
                Clear selection
              </button>
            </div>
          )}
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-3 text-s360-fg3 text-sm border-b border-s360-bg2">
              <SamiSpin className="w-4 h-4 animate-spin" /> Loading orders…
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[1240px]">
              <thead>
                <tr>
                  {[
                    "",
                    "ID",
                    "Distributor",
                    "Order ID",
                    "Sales ID",
                    "PO Number",
                    "Order date",
                    "Order status",
                    "Invoice number",
                    "Total",
                    "Tracking number",
                    "Actions",
                  ].map((label, i) => (
                    <th
                      key={i}
                      className={`text-left py-3 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap ${
                        label === "Total" ? "text-right" : ""
                      }`}
                    >
                      {label === "" ? (
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          className="w-[15px] h-[15px] align-middle accent-s360-violet cursor-pointer"
                        />
                      ) : (
                        label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => {
                  const isSelected = selectedOrders.includes(order.id);
                  const statusText = getOrderStatus(order);
                  const statusMeta = STATUS_PILLS[categorizeStatus(statusText)];
                  const distName = getDistributorName(order);
                  const wm = DIST_WATERMARK[distName] || {
                    short: (distName || "?").slice(0, 3),
                    bg: "bg-gray-400",
                  };
                  const trackingNumber =
                    order?.mouser_data?.tracking_number ||
                    order?.digikey_data?.tracking_number ||
                    order?.element14_data?.tracking_number ||
                    order?.tme_data?.tracking_number ||
                    order?.rutronik_data?.tracking_number ||
                    order?.ti_data?.tracking_number ||
                    order?.arrow_data?.tracking_number ||
                    order?.tti_data?.tracking_number ||
                    order?.avnet_data?.tracking_number ||
                    order?.verical_data?.tracking_number;
                  const isElement14 = !!order?.element14_data;
                  // Email-fulfilled distributors: no live order API to refresh against.
                  const isTme =
                    !!order?.tme_data ||
                    !!order?.rutronik_data ||
                    !!order?.arrow_data ||
                    !!order?.tti_data ||
                    !!order?.avnet_data ||
                    !!order?.verical_data;
                  return (
                    <tr
                      key={order.id}
                      className={`border-b border-s360-bg2 last:border-0 ${
                        isSelected ? "bg-s360-violet-50" : "hover:bg-s360-stripe"
                      }`}
                    >
                      <td className="py-3.5 px-3.5 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectOrder(order.id)}
                          className="w-[15px] h-[15px] align-middle accent-s360-violet cursor-pointer"
                        />
                      </td>
                      <td className="py-3.5 px-3.5">
                        <span className="font-bold text-[13px] text-s360-fg1 tabular-nums">
                          {order?.order_data?.order_id || `${order.id}`}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={`w-[26px] h-[26px] rounded-[5px] inline-flex items-center justify-center text-[10px] font-bold text-white ${wm.bg}`}
                          >
                            {wm.short}
                          </span>
                          {distName || "—"}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5">
                        <span
                          onClick={() => handleViewDetails(order)}
                          className="font-mono text-[13px] font-semibold text-s360-violet cursor-pointer hover:underline"
                        >
                          {order?.web_order_id ||
                            order?.element14_data?.order_id ||
                            order?.tme_data?.order_id ||
                            order?.rutronik_data?.order_id ||
                            order?.ti_data?.order_id ||
                            order?.arrow_data?.order_id ||
                            order?.tti_data?.order_id ||
                            order?.avnet_data?.order_id ||
                            order?.verical_data?.order_id ||
                            "N/A"}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5">
                        <span className="font-mono text-xs text-s360-fg2 tabular-nums whitespace-nowrap">
                          {order?.mouser_data?.sales_order_id ||
                            order?.digikey_data?.sales_order_id ||
                            order?.element14_data?.sales_order_id ||
                            order?.tme_data?.sales_order_id ||
                            order?.rutronik_data?.sales_order_id ||
                            order?.ti_data?.sales_order_id ||
                            order?.arrow_data?.sales_order_id ||
                            order?.tti_data?.sales_order_id ||
                            order?.avnet_data?.sales_order_id ||
                            order?.verical_data?.sales_order_id ||
                            "N/A"}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5">
                        <span className="font-mono text-xs text-s360-fg2 tabular-nums whitespace-nowrap">
                          {order?.mouser_data?.po_number ||
                            order?.digikey_data?.order_id ||
                            order?.element14_data?.po_number ||
                            order?.tme_data?.po_number ||
                            order?.rutronik_data?.po_number ||
                            order?.ti_data?.po_number ||
                            order?.arrow_data?.po_number ||
                            order?.tti_data?.po_number ||
                            order?.avnet_data?.po_number ||
                            order?.verical_data?.po_number ||
                            "N/A"}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5">
                        <span className="font-mono text-xs text-s360-fg2 tabular-nums">
                          {formatDate(order.created_at)}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${statusMeta.pill}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${statusMeta.dot}`} />
                          {statusText}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5">
                        <span className="font-mono text-xs text-s360-fg2">
                          {order?.mouser_data?.invoice_number ||
                            order?.digikey_data?.invoice_id ||
                            "N/A"}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5 text-right">
                        <span className="font-mono text-sm font-bold text-s360-ink tabular-nums">
                          {getTotalAmount(order)}
                        </span>
                      </td>
                      <td className="py-3.5 px-3.5">
                        {trackingNumber ? (
                          <a
                            href={`https://mydhl.express.dhl/us/en/tracking.html#/results?id=${trackingNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs font-semibold text-s360-violet hover:text-s360-violet-600 hover:underline whitespace-nowrap"
                          >
                            {trackingNumber}
                          </a>
                        ) : (
                          <span className="font-mono text-xs text-s360-fg-disabled">—</span>
                        )}
                      </td>
                      <td className="py-3.5 px-3.5">
                        <div className="inline-flex items-center gap-1.5">
                          {!isElement14 && !isTme && (
                            <button
                              onClick={() => handleRefresh(order)}
                              disabled={refreshingId === order.id}
                              title="Refresh from distributor"
                              className="w-[30px] h-[30px] rounded-[5px] inline-flex items-center justify-center text-white bg-s360-fg2 hover:bg-s360-fg1 disabled:opacity-60"
                            >
                              {refreshingId === order.id ? (
                                <SamiSpin className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCcw className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleDownload(order)}
                            disabled={downloadingId === order.id}
                            title="Download invoice"
                            className="w-[30px] h-[30px] rounded-[5px] inline-flex items-center justify-center text-white bg-s360-success hover:bg-[#0B5A0B] disabled:opacity-60"
                          >
                            {downloadingId === order.id ? (
                              <SamiSpin className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleViewDetails(order)}
                            title="View order"
                            className="h-[30px] px-3 rounded-[5px] inline-flex items-center gap-1.5 text-white bg-s360-violet hover:bg-s360-violet-600 text-xs font-semibold"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan="12" className="py-8 text-center text-s360-fg3">
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-lg font-medium text-s360-fg1">
                          No orders found
                        </div>
                        <div className="text-sm">
                          {orderData.length === 0
                            ? "You haven't placed any orders yet"
                            : "No orders match the current filter."}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={(open) => setShowModal(open)}>
        <DialogContent className="sm:max-w-[550px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="text-primary">Distributors</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4 pt-4 pb-1.5">
              <div className="space-y-2">
                <Label htmlFor="allDistributors">
                  Select Distributors{" "}
                  <span className="text-red-600">*</span>{" "}
                </Label>
                <Select
                  value={distributor}
                  onValueChange={handleSelectDistributor}
                >
                  <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                    <SelectValue placeholder="Select Distributors" />
                  </SelectTrigger>
                  <SelectContent>
                    {All_DISTRIBUTOR.filter(
                      (distributor) => distributor.value !== "Element14",
                    ).map((distributor) => (
                      <SelectItem
                        key={distributor.value}
                        value={distributor.value}
                      >
                        {distributor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {distributor && (
                <div className="space-y-1">
                  <Label htmlFor="saleId">
                    Enter Order Id <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="saleId"
                    type="text"
                    value={saleId || ""}
                    onChange={(e) => setSaleId(e.target.value)}
                    placeholder="Enter Order Id"
                    className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
              )}
              {error && <div className="text-red-600 text-sm">{error}</div>}
            </div>
            <DialogFooter className="pt-6">
              <Button
                variant="secondary"
                className="hover:bg-gray-300 border border-gray-300"
                type="button"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loader}>
                Submit {loader && <SamiSpin className="w-4 h-4 animate-spin" />}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-gray-700">
            Are you sure you want to delete{" "}
            <strong>{selectedOrders.length}</strong> selected order
            {selectedOrders.length !== 1 ? "s" : ""}? This action cannot be
            undone.
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              className="hover:bg-gray-300 border border-gray-300"
              onClick={handleCancelDelete}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={loader}
            >
              Delete {loader && <SamiSpin className="w-4 h-4 animate-spin" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );
};

export default MyOrdersPage;
